import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, ArrowRight } from "@phosphor-icons/react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";

const CartDrawer = () => {
  const { items, open, setOpen, removeItem, updateQty, total, count } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const checkout = async () => {
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email for your order confirmation");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size })),
        origin_url: window.location.origin,
        email,
      };
      const { data } = await api.post("/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/70 z-[60]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed top-0 right-0 h-full w-full max-w-md bg-[#141416] border-l border-line z-[70] flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
          >
            <div className="flex items-center justify-between p-6 border-b border-line">
              <p className="overline">Your Bag ({count})</p>
              <button onClick={() => setOpen(false)} data-testid="cart-close" className="text-bone">
                <X size={22} weight="thin" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {items.length === 0 && (
                <p className="font-serif text-3xl text-ash italic mt-10">Your bag is empty.</p>
              )}
              {items.map((i) => (
                <div key={i.key} className="flex gap-4" data-testid={`cart-item-${i.product_id}`}>
                  <div className="w-20 h-28 bg-ink-700 overflow-hidden shrink-0">
                    <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <p className="font-serif text-xl text-bone leading-tight">{i.name}</p>
                    <p className="font-mono text-xs text-ash mt-1">SIZE {i.size}</p>
                    <p className="text-bone mt-1">{formatINR(i.price)}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-line">
                        <button onClick={() => updateQty(i.key, i.quantity - 1)} className="p-2 text-ash hover:text-bone">
                          <Minus size={12} />
                        </button>
                        <span className="font-mono text-sm w-8 text-center text-bone">{i.quantity}</span>
                        <button onClick={() => updateQty(i.key, i.quantity + 1)} className="p-2 text-ash hover:text-bone">
                          <Plus size={12} />
                        </button>
                      </div>
                      <button onClick={() => removeItem(i.key)} className="font-mono text-xs text-ash hover:text-crimson uppercase">
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="p-6 border-t border-line">
                <div className="flex justify-between mb-4">
                  <span className="overline">Subtotal</span>
                  <span className="font-serif text-3xl text-bone">{formatINR(total)}</span>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="checkout-email"
                  placeholder="Email for order confirmation"
                  className="w-full bg-transparent border border-line px-3 py-2.5 text-bone focus:border-crimson outline-none font-mono text-xs mb-4"
                />
                <button
                  onClick={checkout}
                  disabled={loading}
                  data-testid="checkout-button"
                  className="btn-fill w-full py-4 flex items-center justify-center gap-3 overline text-sm disabled:opacity-50"
                >
                  {loading ? "Redirecting…" : "Proceed to Checkout"} <ArrowRight size={16} />
                </button>
                <p className="font-mono text-[10px] text-ash mt-3 text-center">SECURE PAYMENT VIA STRIPE · INR</p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
